import React, { useState } from 'react';
import { API_BASE_URL, getAuthToken } from '../services/api';

export interface PdfReport {
  id: string;
  title: string;
  fileName: string;
  pageCount: number;
  sizeBytes: number;
  version: number;
  createdAt: string;
  driveLink?: string;
}

interface Props {
  report: PdfReport;
  /** Public share links: viewer is unauthenticated — download/save disabled. */
  readOnly?: boolean;
}

const formatSize = (bytes: number): string => {
  if (!bytes || bytes <= 0) return '';
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

const formatDate = (iso: string): string => {
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '';
  }
};

/**
 * Card for branded PDF reports generated in-house (create_pdf_report /
 * create_campaign_snapshot_pdf). The PDF lives in private Firebase Storage, so
 * Download goes through the authenticated proxy — never a public URL.
 */
const PdfReportCard: React.FC<Props> = ({ report, readOnly = false }) => {
  const [downloading, setDownloading] = useState(false);
  const [savingToDrive, setSavingToDrive] = useState(false);
  const [driveLink, setDriveLink] = useState<string | undefined>(report.driveLink);
  const [error, setError] = useState<string | null>(null);

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    setError(null);
    try {
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/chat/pdf-reports/${report.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || `Download failed (${response.status})`);
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = report.fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('[PdfReportCard] download failed:', err);
      setError(err?.message || 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  const handleSaveToDrive = async () => {
    if (savingToDrive) return;
    setSavingToDrive(true);
    setError(null);
    try {
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/chat/pdf-reports/${report.id}/save-to-drive`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body.error || `Save to Drive failed (${response.status})`);
      }
      if (body.driveLink) setDriveLink(body.driveLink);
    } catch (err: any) {
      console.error('[PdfReportCard] save to Drive failed:', err);
      setError(err?.message || 'Save to Drive failed');
    } finally {
      setSavingToDrive(false);
    }
  };

  const metaParts = [
    report.version > 1 ? `v${report.version}` : null,
    `${report.pageCount} page${report.pageCount === 1 ? '' : 's'}`,
    formatSize(report.sizeBytes),
    formatDate(report.createdAt),
  ].filter(Boolean);

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <span style={styles.badge}>PDF REPORT</span>
        <span style={styles.meta}>{metaParts.join(' · ')}</span>
      </div>

      <div style={styles.title}>{report.title}</div>

      <div style={styles.actions}>
        {!readOnly && (
          <button type="button" style={styles.primaryButton} onClick={handleDownload} disabled={downloading}>
            {downloading ? 'Downloading…' : 'Download PDF'}
          </button>
        )}
        {!readOnly && !driveLink && (
          <button type="button" style={styles.secondaryButton} onClick={handleSaveToDrive} disabled={savingToDrive}>
            {savingToDrive ? 'Saving…' : 'Save to Drive'}
          </button>
        )}
        {driveLink && (
          <a href={driveLink} target="_blank" rel="noopener noreferrer" style={styles.secondaryButton}>
            Open in Drive →
          </a>
        )}
        {readOnly && !driveLink && (
          <span style={styles.readOnlyNote}>Sign in to Culture Genius to download</span>
        )}
      </div>

      {error && <div style={styles.error}>{error}</div>}
    </div>
  );
};

const styles: { [k: string]: React.CSSProperties } = {
  card: {
    marginTop: 12,
    padding: '14px 16px',
    background: 'var(--accent-soft)',
    border: '1px solid var(--border-1)',
    borderRadius: 'var(--radius-md)',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    transition: 'all 220ms var(--ease-standard)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  badge: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.08em',
    padding: '2px 6px',
    borderRadius: 4,
    background: 'var(--accent)',
    color: '#fff',
  },
  meta: {
    fontSize: 12,
    color: 'var(--fg-3)',
  },
  title: {
    fontSize: 15,
    fontWeight: 600,
    color: 'var(--fg-1)',
    lineHeight: 1.3,
  },
  actions: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
    marginTop: 2,
    alignItems: 'center',
  },
  primaryButton: {
    padding: '8px 14px',
    background: 'var(--accent)',
    color: '#fff',
    border: 'none',
    borderRadius: 'var(--radius-sm)',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'var(--font-sans)',
    transition: 'opacity 200ms',
  },
  secondaryButton: {
    padding: '8px 14px',
    background: '#fff',
    color: 'var(--fg-1)',
    border: '1px solid var(--border-2)',
    borderRadius: 'var(--radius-sm)',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    textDecoration: 'none',
    display: 'inline-block',
    fontFamily: 'var(--font-sans)',
    transition: 'all 200ms',
  },
  readOnlyNote: {
    fontSize: 12,
    color: 'var(--fg-3)',
  },
  error: {
    fontSize: 12,
    color: 'var(--danger)',
  },
};

export default PdfReportCard;
